import React, { useEffect, useMemo, useState } from "react";
import {
  getUsuarioByIdRoyal,
  createEnderecoRoyal,
  deleteEnderecoRoyal,
  editEnderecoByEnderecoIdRoyal,
} from "../userService";

const INITIAL_FORM = {
  endereco_id: null,
  endereco_cep: "",
  endereco_rua: "",
  endereco_bairro: "",
  endereco_cidade: "",
  endereco_estado: "",
  endereco_numero: "",
  endereco_complemento: "",
};

export default function Enderecos() {
  const [mode, setMode] = useState("lista");
  const [enderecos, setEnderecos] = useState([]);
  const [principalId, setPrincipalId] = useState(null);
  const [loadingEnderecos, setLoadingEnderecos] = useState(true);
  const [errorEnderecos, setErrorEnderecos] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");
  const [savingEndereco, setSavingEndereco] = useState(false);

  useEffect(() => {
    loadEnderecos();
  }, []);

  async function loadEnderecos() {
    try {
      setLoadingEnderecos(true);
      setErrorEnderecos("");

      const usuarioStorage = JSON.parse(localStorage.getItem("usuario") || "{}");
      const usuario_id = Number(usuarioStorage?.usuario_id) || 0;

      if (!usuario_id) {
        setErrorEnderecos("usuario_id não encontrado");
        setEnderecos([]);
        return;
      }

      const response = await getUsuarioByIdRoyal(usuario_id);
      const enderecosApi = response?.data?.enderecos || [];

      setEnderecos(enderecosApi);

      const enderecoPrincipal =
        enderecosApi.find(
          (item) =>
            item?.endereco_principal === 1 ||
            item?.endereco_principal === true ||
            item?.principal === 1 ||
            item?.principal === true
        ) || enderecosApi[0];

      setPrincipalId(enderecoPrincipal?.endereco_id || null);
    } catch (error) {
      setErrorEnderecos(error.message || "Erro ao buscar endereços");
      setEnderecos([]);
    } finally {
      setLoadingEnderecos(false);
    }
  }

  function resetForm() {
    setForm(INITIAL_FORM);
    setCepError("");
    setCepLoading(false);
  }

  function handleNovoEndereco() {
    resetForm();
    setMode("novo");
  }

  function handleEditarEndereco(endereco) {
    setForm({
      endereco_id: endereco?.endereco_id || null,
      endereco_cep: endereco?.endereco_cep || "",
      endereco_rua: endereco?.endereco_rua || endereco?.endereco_endereco || "",
      endereco_bairro: endereco?.endereco_bairro || "",
      endereco_cidade: endereco?.endereco_cidade || "",
      endereco_estado: endereco?.endereco_estado || "",
      endereco_numero: endereco?.endereco_numero || "",
      endereco_complemento: endereco?.endereco_complemento || "",
    });

    setCepError("");
    setMode("editar");
  }

  async function handleDeleteEndereco(endereco_id) {
    try {
      await deleteEnderecoRoyal(endereco_id);
      await loadEnderecos();
    } catch (error) {
      setErrorEnderecos(error.message || "Erro ao excluir endereço");
    }
  }

  function handleCancel() {
    resetForm();
    setMode("lista");
  }

  function handleChange(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function handleBuscarCep() {
    try {
      setCepLoading(true);
      setCepError("");

      const cepLimpo = String(form.endereco_cep || "").replace(/\D/g, "");

      if (cepLimpo.length !== 8) {
        setCepError("Digite um CEP válido");
        return;
      }

      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();

      if (!response.ok || data.erro) {
        setCepError("CEP não encontrado");
        return;
      }

      setForm((prev) => ({
        ...prev,
        endereco_cep: cepLimpo,
        endereco_rua: data.logradouro || "",
        endereco_bairro: data.bairro || "",
        endereco_cidade: data.localidade || "",
        endereco_estado: data.uf || "",
      }));
    } catch (error) {
      setCepError("Erro ao consultar CEP");
    } finally {
      setCepLoading(false);
    }
  }

  async function handleSalvar() {
    if (!form.endereco_cep || !form.endereco_rua) {
      setCepError("Consulte um CEP válido antes de salvar");
      return;
    }

    if (!form.endereco_numero) {
      setCepError("Preencha o número");
      return;
    }

    try {
      setSavingEndereco(true);
      setCepError("");
      setErrorEnderecos("");

      const usuarioStorage = JSON.parse(localStorage.getItem("usuario") || "{}");
      const usuario_id = Number(usuarioStorage?.usuario_id) || 0;

      if (!usuario_id) {
        setCepError("usuario_id não encontrado");
        return;
      }

      const payload = {
        usuario_id,
        endereco_nome: "",
        endereco_estado: form.endereco_estado,
        endereco_endereco: form.endereco_rua,
        endereco_numero: form.endereco_numero,
        endereco_complemento: form.endereco_complemento,
        endereco_bairro: form.endereco_bairro,
        endereco_cidade: form.endereco_cidade,
        endereco_cep: form.endereco_cep,
      };

      if (mode === "novo") {
        await createEnderecoRoyal(payload);
        await loadEnderecos();
        resetForm();
        setMode("lista");
        return;
      }

      if (mode === "editar") {
        await editEnderecoByEnderecoIdRoyal({
          endereco_id: form.endereco_id,
          endereco_nome: "",
          endereco_estado: form.endereco_estado,
          endereco_endereco: form.endereco_rua,
          endereco_numero: form.endereco_numero,
          endereco_complemento: form.endereco_complemento,
          endereco_bairro: form.endereco_bairro,
          endereco_cidade: form.endereco_cidade,
          endereco_cep: form.endereco_cep,
        });

        await loadEnderecos();
        resetForm();
        setMode("lista");
      }
    } catch (error) {
      setCepError(error.message || "Erro ao salvar endereço");
    } finally {
      setSavingEndereco(false);
    }
  }

  const tituloFormulario = useMemo(() => {
    if (mode === "novo") return "Novo endereço";
    if (mode === "editar") return "Editar endereço";
    return "Endereços";
  }, [mode]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
        alignSelf: "stretch",
        overflowX: "hidden",
        overflowY: "auto",
        maxHeight: "100%",
      }}
    >
      {mode === "lista" && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 600,
              }}
            >
              Endereços
            </h2>

            <button onClick={handleNovoEndereco} style={buttonPrimaryStyle}>
              Adicionar novo endereço
            </button>
          </div>

          {loadingEnderecos && <p style={{ margin: 0 }}>Carregando...</p>}

          {!loadingEnderecos && errorEnderecos && (
            <p style={{ margin: 0, color: "#b91c1c" }}>{errorEnderecos}</p>
          )}

          {!loadingEnderecos && !errorEnderecos && enderecos.length === 0 && (
            <p style={{ margin: 0 }}>Nenhum endereço encontrado.</p>
          )}

          {!loadingEnderecos &&
            !errorEnderecos &&
            enderecos.map((endereco, index) => {
              const isPrincipal = principalId === endereco?.endereco_id;

              return (
                <div
                  key={endereco?.endereco_id || index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    border: "1px solid #e6e9ee",
                    borderRadius: "12px",
                    padding: "16px",
                    backgroundColor: "#fff",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "12px",
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: 600,
                          wordBreak: "break-word",
                        }}
                      >
                        {endereco?.endereco_rua || endereco?.endereco_endereco || "-"},{" "}
                        {endereco?.endereco_numero || "-"}
                      </p>
                    </div>

                    <div
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor: isPrincipal ? "#111827" : "#f3f4f6",
                        color: isPrincipal ? "#fff" : "#111827",
                        flexShrink: 0,
                      }}
                    >
                      {isPrincipal ? "Principal" : "Secundário"}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    {!isPrincipal && (
                      <button
                        onClick={() => setPrincipalId(endereco?.endereco_id)}
                        style={buttonSecondaryStyle}
                      >
                        Definir como principal
                      </button>
                    )}

                    <button
                      onClick={() => handleEditarEndereco(endereco)}
                      style={buttonSecondaryStyle}
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => handleDeleteEndereco(endereco?.endereco_id)}
                      style={buttonSecondaryStyle}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              );
            })}
        </>
      )}

      {(mode === "novo" || mode === "editar") && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 600,
              }}
            >
              {tituloFormulario}
            </h2>

            <button onClick={handleCancel} style={buttonSecondaryStyle}>
              Voltar
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              border: "1px solid #e6e9ee",
              borderRadius: "12px",
              padding: "16px",
              backgroundColor: "#fff",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <div style={{ flex: "1 1 220px", minWidth: "220px", maxWidth: "100%" }}>
                <label style={labelStyle}>CEP</label>
                <input
                  value={form.endereco_cep}
                  onChange={(e) => handleChange("endereco_cep", e.target.value)}
                  style={inputStyle}
                  placeholder="Digite o CEP"
                />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <button
                  onClick={handleBuscarCep}
                  style={buttonPrimaryStyle}
                  disabled={cepLoading}
                >
                  {cepLoading ? "Buscando..." : "Buscar CEP"}
                </button>
              </div>
            </div>

            {cepError ? (
              <p style={{ margin: 0, color: "#b91c1c" }}>{cepError}</p>
            ) : null}

            <div style={{ width: "100%" }}>
              <label style={labelStyle}>Rua</label>
              <input value={form.endereco_rua} disabled style={inputDisabledStyle} />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <div style={{ flex: "1 1 220px", minWidth: "220px", maxWidth: "100%" }}>
                <label style={labelStyle}>Bairro</label>
                <input
                  value={form.endereco_bairro}
                  disabled
                  style={inputDisabledStyle}
                />
              </div>

              <div style={{ flex: "1 1 220px", minWidth: "220px", maxWidth: "100%" }}>
                <label style={labelStyle}>Cidade</label>
                <input
                  value={form.endereco_cidade}
                  disabled
                  style={inputDisabledStyle}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <div style={{ flex: "1 1 120px", minWidth: "120px", maxWidth: "100%" }}>
                <label style={labelStyle}>Estado</label>
                <input
                  value={form.endereco_estado}
                  disabled
                  style={inputDisabledStyle}
                />
              </div>

              <div style={{ flex: "1 1 160px", minWidth: "160px", maxWidth: "100%" }}>
                <label style={labelStyle}>Número</label>
                <input
                  value={form.endereco_numero}
                  onChange={(e) => handleChange("endereco_numero", e.target.value)}
                  style={inputStyle}
                  placeholder="Número"
                />
              </div>
            </div>

            <div style={{ width: "100%" }}>
              <label style={labelStyle}>Complemento</label>
              <input
                value={form.endereco_complemento}
                onChange={(e) =>
                  handleChange("endereco_complemento", e.target.value)
                }
                style={inputStyle}
                placeholder="Complemento"
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <button
                onClick={handleSalvar}
                style={buttonPrimaryStyle}
                disabled={savingEndereco}
              >
                {savingEndereco ? "Salvando..." : "Salvar"}
              </button>

              <button onClick={handleCancel} style={buttonSecondaryStyle}>
                Cancelar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "12px",
  color: "#6b7280",
};

const inputStyle = {
  width: "100%",
  height: "42px",
  border: "1px solid #d1d5db",
  borderRadius: "10px",
  padding: "0 12px",
  boxSizing: "border-box",
  outline: "none",
  backgroundColor: "#fff",
};

const inputDisabledStyle = {
  ...inputStyle,
  backgroundColor: "#f9fafb",
  color: "#6b7280",
};

const buttonPrimaryStyle = {
  height: "42px",
  border: "none",
  borderRadius: "10px",
  padding: "0 16px",
  backgroundColor: "#111827",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 600,
};

const buttonSecondaryStyle = {
  height: "42px",
  border: "1px solid #d1d5db",
  borderRadius: "10px",
  padding: "0 16px",
  backgroundColor: "#fff",
  color: "#111827",
  cursor: "pointer",
  fontWeight: 600,
};